import { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import Layout from '@/layouts';
import ArticleDetail from '@/components/ArticleDetail';
import { AppSettings } from '@/shared/app-common';
import {
  fetchHairstyleArticleData,
  fetchHairstyleLibRandomData,
  getArticleDetailContent,
  getArticleRecommendList,
  getArticleRecommendTags,
  getNewArticleDetailData
} from '@/lib/service';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { canonicalUrl, imgUrl } from '@/shared/utils';
import CustomHead from '@/components/Common/CustomHead';
import { getTdkApi } from '@/lib/service/tdk';
import { blogDetailJsonLd } from '@/lib/jsonld';
import * as React from 'react';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params, locale, req, resolvedUrl } = context;
  const { id } = params;
  const articleDetailRes = await getNewArticleDetailData(`${id}`, locale);
  let detailContent: any;

  if (articleDetailRes?.data?.content_url != undefined) {
    detailContent = await getArticleDetailContent(articleDetailRes?.data?.content_url ?? '');
  }

  const articleRecommendRes = await getArticleRecommendList(
    { currentId: `${articleDetailRes?.data?.article_id}` },
    locale
  );
  const localeRes = await serverSideTranslations(locale, ['common']);
  const recommendTags = await getArticleRecommendTags({ currentId: `${articleDetailRes?.data?.article_id}` }, locale);
  const randomParams = {
    articleId: `${id}`
  };
  const randomHairRes = await fetchHairstyleLibRandomData('article', randomParams);

  let hairArticleRes: any;
  if (detailContent?.data?.keyword != undefined) {
    hairArticleRes = await fetchHairstyleArticleData(detailContent?.data?.keyword, locale);
  }
  const tdkData = await getTdkApi(resolvedUrl, locale);
  const jsonLd = blogDetailJsonLd(
    {
      title: tdkData?.data?.title ?? articleDetailRes?.data?.title,
      desc: tdkData?.data?.description ?? articleDetailRes?.data?.description,
      url: canonicalUrl(req.headers.host, resolvedUrl, locale)
    },
    {
      url: canonicalUrl(req.headers.host, '/blog', locale)
    },
    {
      url: canonicalUrl(req.headers.host, '/', locale)
    }
  );

  return {
    props: {
      ...localeRes,
      model: { ...articleDetailRes?.data },
      contentData: { ...detailContent?.data },
      articleList: articleRecommendRes?.data,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      recommendTags: recommendTags?.data,
      randomHairList: [...(randomHairRes?.data ?? [])],
      hairArticleData: { ...hairArticleRes?.data },
      title: tdkData?.data?.title ?? articleDetailRes?.data?.title,
      description: tdkData?.data?.description ?? articleDetailRes?.data?.description,
      jsonLd
    }
  };
};

const ArticleDetailPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return <ArticleDetail data={props} />;
};

ArticleDetailPage.getLayout = function getLayout(page: React.ReactElement) {
  const contentData = page.props?.contentData;
  const title = page.props?.title ?? contentData?.title;
  const desc = page.props?.description ?? contentData?.introduction;

  return (
    <Layout
      head={
        <CustomHead>
          <title>{`${title} | ${AppSettings.name}`}</title>
          <meta name="description" content={desc || AppSettings.desc} />
          <meta name="keywords" content={`${contentData?.tags}`} />
          <meta property="og:title" content={contentData?.title} />
          <meta
            property="og:description"
            content={contentData.description ? contentData.description : contentData.introduction}
          />
          <meta property="og:image" content={imgUrl(contentData?.image_url, 'mid')} />
          <meta property="og:image:secure_url" content={imgUrl(contentData?.image_url, 'mid')} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={contentData?.title} />
          <meta
            name="twitter:description"
            content={contentData.description ? contentData.description : contentData.introduction}
          />
          <meta name="twitter:image" content={imgUrl(contentData?.image_url, 'mid')} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />

          <link rel="canonical" href={page.props?.herf} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </CustomHead>
      }
    >
      {page}
    </Layout>
  );
};

export default ArticleDetailPage;
